import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Grid,
  SelectChangeEvent,
  Typography,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert
} from '@mui/material';
import { Employee, Positions } from '../types/EmployeeTypes';
import { formatRUT, validateRUT } from '../utils/validation';
import { Project } from '../types/projectTypes';
import { getHistoricalFronts, HistoricalFront, getEmployeeByRut } from '../api/employeeApi';

interface EmployeeFormProps {
  projects: Project[];
  positions: Positions[];
  onAddEmployee: (employee: Omit<Employee, 'id'>, workFrontId?: number) => Promise<void> | void;
  setSelectedProject: (x: boolean) => void;
  setIdProject: (x: number) => void;
  reportDate?: string;
  idProject?: number | null;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({
  projects,
  positions,
  onAddEmployee,
  setSelectedProject,
  setIdProject,
  reportDate,
  idProject
}) => {
  const [isForeigner, setIsForeigner] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isExistingEmployee, setIsExistingEmployee] = useState(false);

  const [formData, setFormData] = useState<Omit<Employee, 'id'> & { projectIdTemp: string | number }>({
    rut: '', name: '', firstName: '', lastName: '', position: '', project: undefined, status: null, projectIdTemp: '', observations: '',
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [errorRutMessage, setErrorRutMessage] = useState('');
  const [formErrors, setFormErrors] = useState({
    rut: false, name: false, firstName: false, position: false, project: false
  });

  const [availableFronts, setAvailableFronts] = useState<HistoricalFront[]>([]);
  const [selectedFrontId, setSelectedFrontId] = useState<number | string>('');

  // 1. EFECTO: Cargar Frentes
  useEffect(() => {
    if (reportDate && idProject) {
        getHistoricalFronts(idProject, reportDate)
            .then(data => setAvailableFronts(data))
            .catch(err => console.error("Error cargando frentes", err));
    } else {
        setAvailableFronts([]);
        setSelectedFrontId('');
    }
  }, [reportDate, idProject]);

  useEffect(() => {
    const checkEmployeeExistence = async () => {
      // CORRECCIÓN: Aseguramos que rut sea string
      const currentRut = formData.rut || '';

      // Solo ejecutamos si hay fecha (histórico) y el RUT parece completo
      if (reportDate && currentRut.length > 8) {

          const isValid = isForeigner ? currentRut.length > 5 : validateRUT(currentRut);

          if (isValid) {
             try {
                 const employeeFound = await getEmployeeByRut(currentRut);

                 if (employeeFound) {
                     setFormData(prev => ({
                         ...prev,
                         name: employeeFound.name,
                         firstName: employeeFound.firstName,
                         lastName: employeeFound.lastName || '',
                         position: employeeFound.position || '',
                     }));
                     setIsExistingEmployee(true);
                     setErrorMessage("");
                 } else {
                     setIsExistingEmployee(false);
                 }
             } catch (error) {
                 setIsExistingEmployee(false);
             }
          }
      } else {
          // Si borra el RUT, desbloqueamos
          if(currentRut.length < 3) setIsExistingEmployee(false);
      }
    };

    const timeoutId = setTimeout(() => {
        checkEmployeeExistence();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.rut, reportDate, isForeigner]);

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };

  const handleForeignerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsForeigner(checked);
    setFormData(prev => ({ ...prev, rut: '' }));
    setIsExistingEmployee(false);
    setErrorRutMessage('');
    setFormErrors(prev => ({ ...prev, rut: false }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (isExistingEmployee && reportDate && ['name', 'firstName', 'lastName', 'position'].includes(name)) {
        return;
    }

    if (name === 'rut') {
      if (isForeigner) {
        const cleanValue = value.toUpperCase().trim();
        if (cleanValue.length > 20) return;
        setFormData({ ...formData, rut: cleanValue });
        setErrorRutMessage('');
        setFormErrors((prevErrors) => ({ ...prevErrors, rut: !cleanValue }));
      } else {
        let formattedRut = formatRUT(value).toUpperCase();
        setFormData({ ...formData, rut: formattedRut });
        if (!validateRUT(formattedRut)) {
          setErrorRutMessage('RUT no válido');
          setFormErrors((prevErrors) => ({ ...prevErrors, rut: true }));
        } else {
          setErrorRutMessage('');
          setFormErrors((prevErrors) => ({ ...prevErrors, rut: false }));
        }
      }
    } else {
      setFormData({ ...formData, [name]: value });
      setFormErrors((prevErrors) => ({ ...prevErrors, [name]: !value }));
    }
  };

  const handleProjectChange = (e: SelectChangeEvent<string | number>) => {
    const selectedValue = e.target.value;
    let newProject: Employee['project'] = null;
    const newStatus: Employee['status'] = 'assigned';
    const projectId = selectedValue as number;
    const selectedProjectObj = projects.find((project) => project.id === projectId);

    if (selectedProjectObj) {
      newProject = selectedProjectObj;
      setIdProject(projectId);
      setSelectedProject(true);
    }
    setFormData(prevData => ({ ...prevData, project: newProject, status: newStatus, projectIdTemp: selectedValue }));
  };

  const handlePositionChange = (event: any, newValue: string | null | undefined) => {
    if (isExistingEmployee && reportDate) return;
    setFormData({ ...formData, position: newValue || '' });
    setFormErrors((prev) => ({ ...prev, position: !newValue }));
  };

  const isProjectMissing = formData.project === undefined;

  const validateForm = () => {
    // CORRECCIÓN: Usar variable segura para RUT también aquí si fuera necesario,
    // aunque aquí el acceso directo suele ser seguro si se valida nulidad.
    const currentRut = formData.rut || '';

    const isRutInvalid = isForeigner ? !currentRut : !currentRut || !validateRUT(currentRut);

    const errors = {
      rut: isRutInvalid,
      name: !formData.name,
      firstName: !formData.firstName,
      position: !formData.position,
      project: !reportDate && isProjectMissing
    };
    setFormErrors(prev => ({ ...prev, ...errors }));
    if (isRutInvalid && !isForeigner && currentRut) setErrorRutMessage('RUT no válido');

    return !Object.values(errors).includes(true);
  };

  const handleAddClick = async () => {
      if (reportDate && !selectedFrontId) {
          setErrorMessage("Para el reporte histórico, es obligatorio seleccionar un Frente de Trabajo.");
          return;
      }

      if (validateForm()) {
        try {
          const transformData: any = { ...formData, active: true };
          if ('projectIdTemp' in transformData) delete transformData.projectIdTemp;

          if (reportDate) {
              await onAddEmployee(transformData as Omit<Employee, 'id'>, Number(selectedFrontId));
          } else {
              await onAddEmployee(transformData as Omit<Employee, 'id'>);
          }

          setOpenSnackbar(true);
          setFormData({ rut: '', name: '', firstName: '', lastName: '', position: '', project: undefined, status: null, observations: '', projectIdTemp: '' });
          setErrorRutMessage('');
          setErrorMessage('');
          setIsForeigner(false);
          setIsExistingEmployee(false);
          setSelectedFrontId('');

        } catch (error) {
          setErrorMessage('Error al agregar empleado. Inténtalo de nuevo.');
        }
      } else {
        setErrorMessage('Por favor, llena todos los campos obligatorios.');
      }
    };

  return (
    <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 2, border: '1px solid #ddd' }}>

      {reportDate && (
        <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
            MODO HISTÓRICO: Editando reporte del {reportDate}
        </Typography>
      )}

      <Grid container columnSpacing={2} rowSpacing={1}>

        {reportDate && (
            <Grid item xs={12} sx={{ mb: 2 }}>
                <FormControl fullWidth color="primary" required>
                    <InputLabel id="front-select-label">Asignar a Frente de Trabajo (Requerido)</InputLabel>
                    <Select
                        labelId="front-select-label"
                        value={selectedFrontId}
                        label="Asignar a Frente de Trabajo (Requerido)"
                        onChange={(e) => setSelectedFrontId(e.target.value)}
                    >
                        {availableFronts.length === 0 ? (
                            <MenuItem disabled>No hay frentes/actividades disponibles para esta fecha</MenuItem>
                        ) : (
                            availableFronts.map((front) => (
                                <MenuItem key={front.id} value={front.id}>
                                    {front.label}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </Grid>
        )}

        <Grid item xs={12} md={2}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Proyecto</InputLabel>
            <Select
              value={formData.projectIdTemp || ''}
              onChange={handleProjectChange}
              label="Proyecto"
              disabled={!!reportDate}
              required={!reportDate}
              displayEmpty
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.costCenter} - {project.name}
                </MenuItem>
              ))}
            </Select>
            {formErrors.project && <Typography color="error">Obligatorio</Typography>}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <Box display="flex" flexDirection="column">
            <TextField
              label={isForeigner ? "Pasaporte / Código" : "RUT"}
              name="rut"
              value={formData.rut}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              error={Boolean(formErrors.rut)}
              helperText={formErrors.rut ? (isForeigner ? 'Campo obligatorio' : 'RUT inválido') : ''}
              required
              inputProps={{ maxLength: isForeigner ? 20 : 12 }}
              color={isExistingEmployee ? "success" : "primary"}
              focused={isExistingEmployee}
            />
            <FormControlLabel
              control={<Checkbox checked={isForeigner} onChange={handleForeignerChange} size="small" color="primary" />}
              label={<Typography variant="caption">Pasaporte</Typography>}
              sx={{ mt: 0.5, ml: 0.5 }}
            />
            <Typography variant="caption" color="error" sx={{ minHeight: '20px' }}>{errorRutMessage}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
              label="Nombres"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              required
              error={formErrors.name}
              disabled={isExistingEmployee && !!reportDate}
              sx={{ backgroundColor: (isExistingEmployee && !!reportDate) ? '#e0e0e0' : 'inherit' }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
              label="Apellido Paterno"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              required
              error={formErrors.firstName}
              disabled={isExistingEmployee && !!reportDate}
              sx={{ backgroundColor: (isExistingEmployee && !!reportDate) ? '#e0e0e0' : 'inherit' }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
              label="Apellido Materno"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              disabled={isExistingEmployee && !!reportDate}
              sx={{ backgroundColor: (isExistingEmployee && !!reportDate) ? '#e0e0e0' : 'inherit' }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth variant="outlined">
            <Autocomplete
              value={formData.position}
              onChange={handlePositionChange}
              options={Array.from(new Set(positions.map((position) => position.description)))}
              disabled={isExistingEmployee && !!reportDate}
              renderInput={(params) => (
                  <TextField
                      {...params}
                      label="Cargo"
                      required
                      error={formErrors.position}
                      sx={{ backgroundColor: (isExistingEmployee && !!reportDate) ? '#e0e0e0' : 'inherit' }}
                  />
              )}
            />
            {formErrors.position && <Typography color="error">Obligatorio</Typography>}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={12}>
          <TextField label="Observaciones" name="observations" value={formData.observations} onChange={handleInputChange} variant="outlined" fullWidth multiline minRows={3} />
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleAddClick} fullWidth>
            {reportDate ? "Agregar al Histórico" : "Agregar"}
          </Button>
        </Grid>

        {errorMessage && (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography color="error">{errorMessage}</Typography>
          </Grid>
        )}
      </Grid>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Operación exitosa
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeForm;